import React, { useState } from "react";
import { useForm } from "@/hooks/form.hook";
import { TextFieldElement } from "@/components/formElements/text-field-element";
import { TextAreaElement } from "@/components/formElements/text-area-element";
import { SelectElement } from "@/components/formElements/select-element";
import { Button } from "@/components/ui/button";
import { LoadingFallback } from "@/components/loading-fallback";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { useOrganisationList } from "@/hooks/organisation.hooks";
import { FilterDialog, OrganisationsList } from "../Organisations";
import { Switch } from "@/components/ui/switch";
import { FilterIcon, SendIcon } from "lucide-react";
export const CreateEditEmail = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const [selectedOrganisation, setSelectedOrganisation] = useState<any>(null);
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [selectedPlanStatuses, setSelectedPlanStatuses] = useState<string[]>(
    []
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const form = useForm({
    fields: {
      from: "james@mergoio.com",
      subject: "",
      extraText: "",
      content: "",
      recipientType: "ALL",
      recipients: [] as number[],
    },
    validate: {
      subject: (value) => (!value ? "Subject is required" : null),
      content: (value) => (!value ? "Content is required" : null),
      recipients: (value) => {
        if (form.state.recipientType === "SELECTED" && value.length === 0) {
          return "Please select at least one organisation";
        }
        return null;
      },
    },
  });

  const handleSubmit = () => {
    if (form.validate()) {
      return;
    }
    console.log({
      ...form.state,
    });
  };

  const handleOrganisationSelect = (orgIds: number[]) => {
    form.setState({
      recipients: orgIds,
    });
  };

  return (
    <div className="min-h-screen flex-1 flex flex-col overflow-hidden">
      <main className="flex-1 bg-background overflow-y-auto min-h-screen pt-10">
        <div className="border-b pb-6">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-2xl font-semibold">New Marketing Email</h2>
              <div className="flex justify-end">
                <Button
                  variant="secondary"
                  className="flex items-center gap-4 justify-between bg-blue-500/20 text-blue-500 w-[200px]"
                  onClick={handleSubmit}
                >
                  <span>Send Email</span>
                  <SendIcon size={14} />
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap">
          {/* Left Column - 1/3 width */}
          <div className="lg:w-1/2 w-full space-y-6 px-6 pt-6 border-r">
            <SelectElement
              fieldName="from"
              label="From"
              form={form}
              options={[
                { value: "james@mergoio.com", label: "james@mergoio.com" },
              ]}
            />

            <TextFieldElement
              fieldName="subject"
              label="Subject"
              placeholder="Enter email subject"
              form={form}
            />

            <TextFieldElement
              fieldName="extraText"
              label="Extra Text"
              placeholder="Enter extra text"
              form={form}
            />

            <div className="border rounded-lg p-6 bg-muted/10">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-primary">
                  Email Recipients
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Determine who will receive this marketing email
                </p>
              </div>

              <div className="flex flex-col space-y-6">
                <div className="flex flex-col justify-between bg-background dark:bg-muted/10 p-4 rounded-lg border dark:border-muted-foreground/20 shadow-sm">
                  <div className="flex flex-wrap items-center gap-3">
                    <Switch
                      checked={form.state.recipientType === "ALL"}
                      onCheckedChange={() => {
                        const newRecipientType =
                          form.state.recipientType === "ALL"
                            ? "SELECTED"
                            : "ALL";
                        form.setState({
                          recipientType: newRecipientType,
                        });

                        if (newRecipientType === "ALL") {
                          form.setState({
                            recipients: [],
                          });
                        }
                      }}
                      className="data-[state=checked]:bg-blue-500"
                    />
                    <div>
                      <p className="font-medium dark:text-white">
                        Send to all organisations
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Email will be sent to all organisations in the system
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200 text-sm px-3 py-1 rounded-full font-medium">
                    {form.state.recipientType === "ALL"
                      ? "All recipients selected"
                      : "Custom selection"}
                  </div>
                </div>

                {form.state.recipientType === "SELECTED" && (
                  <div className="flex flex-col gap-4">
                    <div className="rounded-lg bg-background dark:bg-muted/40 p-4 border-b dark:border-muted-foreground/20">
                      <h4 className="font-medium dark:text-white">
                        Select specific organisations
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Choose which organisations should receive this email
                      </p>
                      <div className="mt-2 text-sm bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 p-2 rounded flex items-center gap-2">
                        <span>⚠️</span>
                        <span>
                          You've selected {form.state.recipients.length}{" "}
                          organisation(s)
                        </span>
                      </div>
                      {form.errors.recipients && (
                        <div className="mt-2 text-sm text-red-500">
                          {form.errors.recipients}
                        </div>
                      )}
                    </div>
                    {form.state.recipientType === "SELECTED" && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                        onClick={() => setIsFilterOpen(true)}
                      >
                        <FilterIcon size={14} />
                        <span>Filters</span>
                      </Button>
                    )}
                    <OrganisationsList
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      selectedOrganisation={selectedOrganisation}
                      setSelectedOrganisation={setSelectedOrganisation}
                      selectedPlans={selectedPlans}
                      selectedPlanStatuses={selectedPlanStatuses}
                      pageSize={pageSize}
                      selectionMode={true}
                      selectedOrganisations={form.state.recipients}
                      onOrganisationSelect={handleOrganisationSelect}
                      columns={[
                        "companyName",
                        "plan",
                        "planStatus",
                        "latestOrderCount",
                      ]}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - 2/3 width */}
          <div className="lg:w-1/2 w-full px-6 pt-6">
            <TextAreaElement
              fieldName="content"
              label="Content"
              placeholder="Enter email content"
              rows={30}
              form={form}
              className="h-full"
            />
          </div>
        </div>
      </main>

      <FilterDialog
        isOpen={isFilterOpen}
        onOpenChange={setIsFilterOpen}
        selectedPlans={selectedPlans}
        setSelectedPlans={setSelectedPlans}
        selectedPlanStatuses={selectedPlanStatuses}
        setSelectedPlanStatuses={setSelectedPlanStatuses}
        onApply={() => setCurrentPage(1)}
      />
    </div>
  );
};
